(() => {
  const SORT_OPTION_VALUE = 'discount-descending';
  const ACTIVE_CLASS = 'is-sale-discount-sort-active';
  const PRODUCTS_JSON_LIMIT = 250;

  let sortedCards = null;
  let currentPage = 1;
  let activeRoot = null;
  let loading = false;
  let abortController = null;
  let renderedEarly = false;
  let saleObserver = null;
  let appending = false;

  function getRoot() {
    return document.querySelector('[data-sale-discount-sort]');
  }

  function getConfig(root) {
    return {
      sectionId: root.dataset.sectionId,
      collectionUrl: root.dataset.collectionUrl,
      productsPerPage: Math.max(1, Number(root.dataset.productsPerPage || 48)),
      productsCount: Math.max(0, Number(root.dataset.productsCount || 0)),
      productPriceStrategy: root.dataset.productPriceStrategy || '',
      sortParam: root.dataset.sortParam || 'dsort',
      sortValue: root.dataset.sortValue || 'discount-desc',
    };
  }

  function dispatchLoading(start) {
    document.documentElement.dispatchEvent(
      new CustomEvent(start ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  function isAbortError(error) {
    return Boolean(error && (error.name === 'AbortError' || error.code === 20));
  }

  function abortActiveFetch() {
    if (!abortController) {
      return;
    }
    abortController.abort();
    abortController = null;
  }

  function disconnectSaleObserver() {
    if (!saleObserver) {
      return;
    }
    saleObserver.disconnect();
    saleObserver = null;
  }

  function prepareSortedCard(card) {
    const clone = card.cloneNode(true);
    clone.removeAttribute('reveal-on-scroll');
    clone.style.opacity = '1';
    clone.style.transform = 'none';
    return clone;
  }

  function isDiscountSortUrl(config) {
    const url = new URL(window.location.href);
    return url.searchParams.get(config.sortParam) === config.sortValue;
  }

  function setDiscountSortUrl(config, enabled) {
    const url = new URL(window.location.href);
    if (enabled) {
      url.searchParams.set(config.sortParam, config.sortValue);
      url.searchParams.delete('page');
      url.searchParams.delete('sort_by');
      // フィルター条件は維持する（消すと「フィルターが効いていない」ように見える）
    } else {
      url.searchParams.delete(config.sortParam);
    }
    history.replaceState({}, '', url.toString());
  }

  function hasActiveFilters(url = new URL(window.location.href)) {
    return [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
  }

  function applyFilterParams(url) {
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        url.searchParams.append(key, value);
      }
    });
    return url;
  }

  function sortIdsByCardDiscount(cardById) {
    return [...cardById.entries()]
      .map(([id, entry]) => ({ id, discount: entry.discount }))
      .sort((a, b) => {
        if (b.discount !== a.discount) {
          return b.discount - a.discount;
        }
        return Number(a.id) - Number(b.id);
      })
      .map((item) => item.id);
  }

  function updateSelectedOption(selected) {
    const options = document.querySelectorAll('#sort-by-popover [role="option"]');
    if (!selected) {
      return;
    }

    options.forEach((option) => {
      option.setAttribute(
        'aria-selected',
        option.getAttribute('value') === SORT_OPTION_VALUE ? 'true' : 'false'
      );
    });
  }

  function setFiltersDisabled(root, disabled) {
    root.classList.toggle(ACTIVE_CLASS, disabled);
    const filterButton = document.querySelector('[aria-controls="facets-drawer"]');
    if (filterButton) {
      filterButton.toggleAttribute('disabled', disabled);
      filterButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      filterButton.style.pointerEvents = disabled ? 'none' : '';
      filterButton.style.opacity = disabled ? '0.4' : '';
    }

    const sidebar = root.querySelector('.facets-sidebar');
    if (sidebar) {
      sidebar.hidden = disabled;
    }
  }

  function withViewParam(url) {
    const current = new URL(window.location.href);
    const view = current.searchParams.get('view');
    if (view) {
      url.searchParams.set('view', view);
    }
    return url;
  }

  function buildSectionPageUrl(config, page) {
    const url = withViewParam(new URL(config.collectionUrl, window.location.origin));
    applyFilterParams(url);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', config.sectionId);
    return url;
  }

  function buildProductsJsonUrl(config, page) {
    const url = new URL(`${config.collectionUrl}/products.json`, window.location.origin);
    url.searchParams.set('limit', String(PRODUCTS_JSON_LIMIT));
    url.searchParams.set('page', String(page));
    return url;
  }

  function pickVariantForDiscount(product, strategy) {
    const variants = (product.variants || []).map((variant) => ({
      price: Number.parseFloat(variant.price),
      compareAtPrice: variant.compare_at_price ? Number.parseFloat(variant.compare_at_price) : 0,
    })).filter((variant) => Number.isFinite(variant.price));

    if (!variants.length) {
      return null;
    }

    const uniquePrices = new Set(variants.map((variant) => variant.price));
    const priceVaries = uniquePrices.size > 1;

    if (priceVaries && strategy !== 'from_price') {
      return variants.reduce((highest, variant) => (
        variant.price >= highest.price ? variant : highest
      ));
    }

    return variants.reduce((lowest, variant) => (
      variant.price <= lowest.price ? variant : lowest
    ));
  }

  function discountPercentFromProduct(product, strategy) {
    const variant = pickVariantForDiscount(product, strategy);
    if (!variant || !(variant.compareAtPrice > variant.price)) {
      return 0;
    }
    return Math.ceil((variant.compareAtPrice - variant.price) * 100 / variant.compareAtPrice);
  }

  function prepareCard(card) {
    const prepared = document.importNode(card, true);
    prepared.removeAttribute('reveal-on-scroll');
    prepared.style.opacity = '1';
    prepared.style.transform = 'none';
    return prepared;
  }

  async function fetchJson(url, signal) {
    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url.pathname}`);
    }
    return response.json();
  }

  async function fetchText(url, signal) {
    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url.pathname}`);
    }
    return response.text();
  }

  async function fetchSortedProductIds(config, signal, onPage) {
    const totalPages = Math.max(1, Math.ceil(config.productsCount / PRODUCTS_JSON_LIMIT));
    const products = [];

    await Promise.all(
      Array.from({ length: totalPages }, (_, index) => {
        const page = index + 1;
        return fetchJson(buildProductsJsonUrl(config, page), signal).then((payload) => {
          products.push(...(payload.products || []));
          if (onPage) {
            onPage();
          }
        });
      })
    );

    return products
      .map((product) => ({
        id: String(product.id),
        discount: discountPercentFromProduct(product, config.productPriceStrategy),
      }))
      .sort((a, b) => {
        if (b.discount !== a.discount) {
          return b.discount - a.discount;
        }
        return Number(a.id) - Number(b.id);
      })
      .map((item) => item.id);
  }

  function initAppendedFavorites() {
    if (typeof window.mxprInitCards === 'function') {
      window.mxprInitCards();
      return;
    }
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  function extractCards(html, cardById) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('product-card[data-product-id]').forEach((card) => {
      const id = card.getAttribute('data-product-id');
      if (!id || cardById.has(id)) {
        return;
      }
      cardById.set(id, {
        node: prepareCard(card),
        discount: Number(card.getAttribute('data-discount-percent') || 0),
      });
    });
  }

  function buildSortedCards(sortedIds, cardById) {
    const ordered = [];

    sortedIds.forEach((id) => {
      const entry = cardById.get(id);
      if (entry) {
        ordered.push(entry);
      }
    });

    // 最終的には一覧表示中の %OFF（data-discount-percent）で確定ソートする
    return ordered
      .sort((a, b) => {
        if (b.discount !== a.discount) {
          return b.discount - a.discount;
        }
        return Number(a.node.getAttribute('data-product-id')) - Number(b.node.getAttribute('data-product-id'));
      })
      .map((item) => item.node);
  }

  function hasCardsForPage(sortedIds, cardById, config, page) {
    const start = (page - 1) * config.productsPerPage;
    const ids = sortedIds.slice(start, start + config.productsPerPage);
    if (!ids.length) {
      return false;
    }
    return ids.every((id) => cardById.has(id));
  }

  function showProgress(root, done, total) {
    const liveRoot = getRoot() || root;
    if (!liveRoot) {
      return;
    }

    const container = liveRoot.querySelector('[data-sale-discount-sort-pagination]');
    if (!container) {
      return;
    }

    const safeTotal = Math.max(1, total);
    const safeDone = Math.max(0, Math.min(done, safeTotal));
    container.innerHTML = `<p class="h6 text-subdued" data-sale-discount-sort-progress role="status" aria-live="polite" style="text-align:center;padding:12px 0;">読み込み中 ${safeDone}/${safeTotal}</p>`;
  }

  function renderPagination(root, config, page, totalPages, { interactive, progress } = {}) {
    const container = root.querySelector('[data-sale-discount-sort-pagination]');
    if (!container) {
      return;
    }

    disconnectSaleObserver();

    if (!interactive) {
      if (progress) {
        showProgress(root, progress.done, progress.total);
      } else {
        container.innerHTML = '<p class="h6 text-subdued" data-sale-discount-sort-progress role="status" aria-live="polite" style="text-align:center;padding:12px 0;">読み込み中...</p>';
      }
      return;
    }

    if (page >= totalPages) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = '<div class="collection-infinite-scroll collection-infinite-scroll--sale"><div class="collection-infinite-scroll__sentinel" data-sale-infinite-sentinel><span class="collection-infinite-scroll__spinner" aria-hidden="true"></span></div></div>';
    const sentinel = container.querySelector('[data-sale-infinite-sentinel]');
    if (!sentinel || typeof IntersectionObserver !== 'function') {
      return;
    }

    saleObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        appendNextSortedPage(root, config);
      }
    }, {
      rootMargin: '400px 0px',
    });
    saleObserver.observe(sentinel);
  }

  function appendNextSortedPage(root, config) {
    if (!sortedCards || !sortedCards.length || loading || appending) {
      return;
    }

    const liveRoot = getRoot() || root;
    const productList = liveRoot.querySelector('product-list');
    if (!productList) {
      return;
    }

    const totalPages = Math.max(1, Math.ceil(sortedCards.length / config.productsPerPage));
    if (currentPage >= totalPages) {
      renderPagination(liveRoot, config, currentPage, totalPages, { interactive: true });
      return;
    }

    appending = true;
    const nextPage = currentPage + 1;
    const start = (nextPage - 1) * config.productsPerPage;
    const pageCards = sortedCards.slice(start, start + config.productsPerPage);
    productList.append(...pageCards.map(prepareSortedCard));
    initAppendedFavorites();
    currentPage = nextPage;
    renderPagination(liveRoot, config, currentPage, totalPages, { interactive: true });
    appending = false;
  }

  function renderPage(root, config, page, { interactive = true, progress = null } = {}) {
    if (!sortedCards || !sortedCards.length) {
      return;
    }

    const liveRoot = getRoot() || root;
    const productList = liveRoot.querySelector('product-list');
    if (!productList) {
      return;
    }

    const totalPages = Math.max(1, Math.ceil(sortedCards.length / config.productsPerPage));
    currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * config.productsPerPage;
    const pageCards = sortedCards.slice(start, start + config.productsPerPage);

    productList.replaceChildren(...pageCards.map(prepareSortedCard));
    initAppendedFavorites();
    renderPagination(liveRoot, config, currentPage, totalPages, { interactive, progress });
    updateSelectedOption(true);
    // フィルター UI は維持（適用中の条件が見えなくなると「効いていない」ように見える）
    setFiltersDisabled(liveRoot, false);
    activeRoot = liveRoot;
  }

  async function loadDiscountSortData(config, signal, onProgress) {
    const cardById = new Map();
    const filtersActive = hasActiveFilters();
    const sectionPages = Math.max(1, Math.ceil(config.productsCount / config.productsPerPage));
    // products.json はストアフロントの filter.* を無視するため、フィルター時は HTML のみ使う
    const jsonPages = filtersActive
      ? 0
      : Math.max(1, Math.ceil(config.productsCount / PRODUCTS_JSON_LIMIT));
    const total = sectionPages + jsonPages;
    let done = 0;
    let sortedIds = null;

    const tick = () => {
      done += 1;
      if (filtersActive) {
        sortedIds = sortIdsByCardDiscount(cardById);
      }
      onProgress({ sortedIds, cardById, done, total });
    };

    onProgress({ sortedIds, cardById, done, total });

    const sectionPromise = Promise.all(
      Array.from({ length: sectionPages }, (_, index) => {
        const page = index + 1;
        return fetchText(buildSectionPageUrl(config, page), signal).then((html) => {
          extractCards(html, cardById);
          tick();
        });
      })
    );

    if (filtersActive) {
      await sectionPromise;
      sortedIds = sortIdsByCardDiscount(cardById);
      return {
        sortedIds: sortedIds || [],
        cardById,
      };
    }

    const productsPromise = fetchSortedProductIds(config, signal, tick).then((ids) => {
      sortedIds = ids;
      onProgress({ sortedIds, cardById, done, total });
      return ids;
    });

    await Promise.all([productsPromise, sectionPromise]);
    return {
      sortedIds: sortedIds || [],
      cardById,
    };
  }

  async function activateDiscountSort(root) {
    abortActiveFetch();
    abortController = new AbortController();
    const { signal } = abortController;

    const config = getConfig(root);
    activeRoot = root;
    loading = true;
    renderedEarly = false;
    sortedCards = null;
    currentPage = 1;
    dispatchLoading(true);
    setDiscountSortUrl(config, true);
    updateSelectedOption(true);
    setFiltersDisabled(root, false);

    const filtersActive = hasActiveFilters();
    const sectionPages = Math.max(1, Math.ceil(config.productsCount / config.productsPerPage));
    const jsonPages = filtersActive
      ? 0
      : Math.max(1, Math.ceil(config.productsCount / PRODUCTS_JSON_LIMIT));
    showProgress(root, 0, sectionPages + jsonPages);

    try {
      const { sortedIds, cardById } = await loadDiscountSortData(
        config,
        signal,
        ({ sortedIds: ids, cardById: cards, done, total }) => {
          const progress = { done, total };

          if (renderedEarly) {
            showProgress(getRoot() || root, done, total);
            return;
          }

          // フィルター時の途中ソートは最終結果とずれるため、全件揃うまで待つ
          if (filtersActive && done < total) {
            showProgress(getRoot() || root, done, total);
            return;
          }

          if (!ids || !ids.length || !hasCardsForPage(ids, cards, config, 1)) {
            showProgress(getRoot() || root, done, total);
            return;
          }

          sortedCards = ids
            .slice(0, config.productsPerPage)
            .map((id) => cards.get(id)?.node)
            .filter(Boolean);
          if (!sortedCards.length) {
            showProgress(getRoot() || root, done, total);
            return;
          }

          renderedEarly = true;
          renderPage(getRoot() || root, config, 1, { interactive: false, progress });
        }
      );

      if (signal.aborted) {
        return;
      }

      sortedCards = buildSortedCards(sortedIds, cardById);
      if (!sortedCards.length) {
        throw new Error('No product cards found for discount sort');
      }

      renderPage(getRoot() || root, config, 1, { interactive: true });
      if (!renderedEarly) {
        (getRoot() || root).scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      console.error('[sale-discount-sort]', error);
      deactivateDiscountSort(getRoot() || root, { keepUrl: false });
    } finally {
      if (abortController && abortController.signal === signal) {
        abortController = null;
        loading = false;
        dispatchLoading(false);
      }
    }
  }

  function deactivateDiscountSort(root, { keepUrl = false } = {}) {
    abortActiveFetch();
    disconnectSaleObserver();
    sortedCards = null;
    currentPage = 1;
    activeRoot = null;
    loading = false;
    renderedEarly = false;
    appending = false;
    if (root) {
      const config = getConfig(root);
      if (!keepUrl) {
        setDiscountSortUrl(config, false);
      }
      setFiltersDisabled(root, false);
    }
  }

  function isDiscountSortEvent(event) {
    const value = event.detail && event.detail.value;
    if (value === SORT_OPTION_VALUE) {
      return true;
    }

    const option = event.target && event.target.closest
      ? event.target.closest('[data-sale-discount-sort-option]')
      : null;
    return Boolean(option);
  }

  function onListboxChange(event) {
    const root = getRoot();
    if (!root) {
      return;
    }

    if (isDiscountSortEvent(event)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      activateDiscountSort(root);
      return;
    }

    if (sortedCards || loading) {
      deactivateDiscountSort(root);
    }
  }

  function onDiscountOptionClick(event) {
    const option = event.target.closest('[data-sale-discount-sort-option]');
    if (!option) {
      return;
    }

    const root = getRoot();
    if (!root) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    activateDiscountSort(root);
  }

  function onClick(event) {
    const pageButton = event.target.closest('[data-sale-discount-page]');
    if (!pageButton || !sortedCards || !activeRoot || loading) {
      return;
    }

    event.preventDefault();
    const page = Number(pageButton.getAttribute('data-sale-discount-page'));
    if (!Number.isFinite(page)) {
      return;
    }

    renderPage(activeRoot, getConfig(activeRoot), page, { interactive: true });
    activeRoot.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function maybeRestoreFromUrl() {
    const root = getRoot();
    if (!root) {
      return;
    }

    const config = getConfig(root);
    if (isDiscountSortUrl(config)) {
      activateDiscountSort(root);
    }
  }

  function preserveDiscountSortOnFacetUpdate(event) {
    const url = event.detail && event.detail.url;
    if (!url) {
      return;
    }

    const root = getRoot();
    if (!root) {
      return;
    }

    const config = getConfig(root);
    const shouldKeep = Boolean(sortedCards || loading || isDiscountSortUrl(config));
    if (!shouldKeep) {
      return;
    }

    url.searchParams.set(config.sortParam, config.sortValue);
    url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    const view = new URL(window.location.href).searchParams.get('view');
    if (view) {
      url.searchParams.set('view', view);
    }

    abortActiveFetch();
    disconnectSaleObserver();
    sortedCards = null;
    currentPage = 1;
    loading = false;
    renderedEarly = false;
    appending = false;
    activeRoot = null;

    const sectionId = url.searchParams.get('section_id') || config.sectionId;
    const shopifySection = document.getElementById(`shopify-section-${sectionId}`);
    if (!shopifySection) {
      return;
    }

    const observer = new MutationObserver(() => {
      observer.disconnect();
      const nextRoot = getRoot();
      if (nextRoot && isDiscountSortUrl(getConfig(nextRoot))) {
        activateDiscountSort(nextRoot);
      }
    });
    observer.observe(shopifySection, { childList: true });
  }

  document.addEventListener('listbox:change', onListboxChange, true);
  document.addEventListener('listbox:select', onListboxChange, true);
  document.addEventListener('click', onDiscountOptionClick, true);
  document.addEventListener('click', onClick);
  document.addEventListener('facet:update', preserveDiscountSortOnFacetUpdate, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRestoreFromUrl);
  } else {
    maybeRestoreFromUrl();
  }
})();
